import React from "react";
import { Box, Link, Card as MuiCard, Typography } from "@alpha/ui-lib/ui/external";
import { makeStyles } from "@alpha/ui-lib/theme";
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { components } from "generated/payment-initialisation/schema";
import { useTranslation } from "react-i18next";

const Card = MuiCard as React.FC<React.ComponentProps<typeof MuiCard>>;

export const useStyles = makeStyles(
  () => ({
    migrationTasks: {
      padding: '16px',
      overflow: 'hidden',
      minWidth: 0,
    },
    migrationInfoBox: {
      display: 'flex',
      flexDirection: 'column',
      padding: '4px 12px',
      backgroundColor: '#F7F7F7',
      border: '1px solid #EAEAEA',
      borderRadius: '5px',
    },
    migrationInfoItem: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      justifyContent: 'space-between',
      margin: '8px 0px',
    },
    migrationTaskItem: {
      display: 'flex',
      flexDirection: 'column',
      padding: '12px',
      marginBottom: '8px',
      border: '1px solid #EAEAEA',
      borderRadius: '5px',
    },
    migrationTaskItemTitle: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    migrationTaskItemDescription: {
      marginTop: '16px',
    },
    migrationTaskItemCta: {
      float: 'right',
      textDecoration: 'underline',
      marginTop: '16px',
      cursor: 'pointer',
    },
    }),
    { name: 'MigrationTasks' },
);
  
interface IProps {
  migrationReadiness: components['schemas']['MigrationReadinessResponse'];
  accountInfo: any;
  onNavigateToTab: (tabIndex: number) => void;
}

const MigrationTasks: React.FC<IProps> = ({ migrationReadiness, accountInfo, onNavigateToTab }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const migrationInfo = [
    {
      label: t('migration_wave'),
      value: accountInfo.corpayMigrationWave,
    },
    {
      label: t('migration_date'),
      value: accountInfo.corpayMigrationStartDate ? new Date(accountInfo.corpayMigrationStartDate).toLocaleDateString() : '-',
    },
  ];

  const migrationTasks = [
    {
      title: t('beneficiaries_missing_data'),
      description: t('some_of_your_beneficiaries_are_missing_information_required_for_the_migration'),
      progress: migrationReadiness?.TotalBeneficiariesReadinessPercentage ?? '-',
      ctaLabel: t('fix_now'),
      onCtaClick: () => onNavigateToTab(1),
    },
    {
      title: t('remitters_missing_data'),
      description: t('some_of_your_remitters_are_missing_information_required_for_the_migration'),
      progress: migrationReadiness?.TotalRemittersReadinessPercentage ?? '-',
      ctaLabel: t('fix_now'),
      onCtaClick: () => onNavigateToTab(2),
    },
  ];

  const renderMigrationTasks = () => {
    return (
      <>
        {migrationTasks.map((task) => (
          <Box className={classes.migrationTaskItem}>
            <span className={classes.migrationTaskItemTitle}>
              <Typography variant="body1">{task.title}</Typography>
              <div>{task.progress !== 100 ? <Typography variant="body1">{task.progress}%</Typography> : <FontAwesomeIcon icon={faCircleCheck} color="#55B171" />}</div>
            </span>
            <span className={task.description && task.progress !== 100 ? classes.migrationTaskItemDescription : undefined}>{task.description && task.progress !== 100 ? task.description : null}</span>
            <span style={{ display: task.progress === 100 ? 'none' : 'auto' }}>
              <Link to="#" onClick={task.onCtaClick} className={classes.migrationTaskItemCta}>{task.ctaLabel} <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '4px' }} /></Link>
            </span>
          </Box>
        ))}
      </>
    )
  }

  return (
    <Card className={classes.migrationTasks}>
      <Box className={classes.migrationInfoBox}>
        {migrationInfo.map((info) => (
          <Box className={classes.migrationInfoItem}>
            <span>{info.label}</span>
            <span>{info.value}</span>
          </Box>
        ))}
      </Box>
      <Typography variant="body1" style={{ margin: '14px 0px' }}>{t('migration_tasks')}:</Typography>
      {renderMigrationTasks()}
    </Card>
  )
}

export default MigrationTasks;